import React, { useState } from 'react';
import { Inbox, Mail, Check } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';

interface Option {
  value: string;
  label: string;
  icon: string;
}

interface StepProps {
  result: {
    ui_step: string;
    options: Option[];
  };
  onSelect: (value: string, label: string) => void;
}

const SlackIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zm2.521-10.123a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </svg>
);

const ICON_COMPONENTS: Record<string, React.ReactNode> = {
  inbox: <Inbox className="h-5 w-5" />,
  slack: <SlackIcon className="h-5 w-5" />,
  email: <Mail className="h-5 w-5" />,
};

const DestinationSelectorStep: React.FC<StepProps> = ({ result, onSelect }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);

  const toggleOption = (value: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size === 0 || submitted) return;
    setSubmitted(true);
    const selectedOptions = result.options.filter((o) => selected.has(o.value));
    const labels = selectedOptions.map((o) => o.label).join(', ');
    const values = selectedOptions.map((o) => o.value).join(', ');
    onSelect(values, labels);
  };

  if (submitted) {
    return (
      <div className="w-full max-w-lg p-4 rounded-lg border border-violet-200 bg-violet-50">
        <div className="flex items-center gap-2 text-violet-700">
          <Check className="h-4 w-4" />
          <span className="text-sm font-medium">
            Sending to:{' '}
            {result.options
              .filter((o) => selected.has(o.value))
              .map((o) => o.label)
              .join(', ')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg">
      <p className="text-sm font-medium text-gray-700 mb-3">
        Where should alerts be sent? (select all that apply)
      </p>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {result.options.map((option) => {
          const isSelected = selected.has(option.value);
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => toggleOption(option.value)}
              className={cn(
                'flex flex-col items-center gap-2 p-3 rounded-lg border text-center transition-all',
                isSelected
                  ? 'border-violet-500 bg-violet-50 text-violet-700 ring-1 ring-violet-500'
                  : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
              )}
            >
              <div className={cn('transition-colors', isSelected ? 'text-violet-600' : 'text-gray-500')}>
                {ICON_COMPONENTS[option.icon] || <Inbox className="h-5 w-5" />}
              </div>
              <span className="text-xs font-medium">{option.label}</span>
              {isSelected && (
                <div className="w-4 h-4 rounded-full bg-violet-600 flex items-center justify-center">
                  <Check className="h-2.5 w-2.5 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={handleConfirm}
        disabled={selected.size === 0}
        className={cn(
          'w-full py-2 rounded-md text-sm font-medium transition-colors',
          selected.size > 0
            ? 'bg-violet-600 text-white hover:bg-violet-700'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        )}
      >
        Continue
      </button>
    </div>
  );
};

export default DestinationSelectorStep;
